import { useQuery } from "@tanstack/react-query";
import { useTheme } from "@/components/theme-provider";
import { apiFetch } from "@/lib/api-client";

interface SiteSettings {
  incentiveImageUrl?: string | null;
  logoDesktopLight: string | null;
  logoDesktopDark: string | null;
  logoMobileLight: string | null;
  logoMobileDark: string | null;
  logoHeightDesktopLight: number;
  logoHeightDesktopDark: number;
  logoHeightMobileLight: number;
  logoHeightMobileDark: number;
  landing_hero_desktop_url?: string | null;
  landing_hero_mobile_url?: string | null;
  dailyOpenBeta?: boolean;
  predictionsBetaOnly?: boolean;
  zapBetaOnly?: boolean;
  crateBetaOnly?: boolean;
  liveRoomsEnabled?: boolean;
}

export function useSiteSettings() {
  const { data: settings, isLoading } = useQuery<SiteSettings>({
    queryKey: ['/api/site-settings/public'],
    queryFn: async () => {
      try {
        console.log('[Site Settings] Fetching from API...');
        const response = await apiFetch('/api/site-settings/public');
        console.log('[Site Settings] Response status:', response.status);
        
        if (!response.ok) {
          console.error('[Site Settings] Failed to fetch:', response.status, response.statusText);
          throw new Error('Failed to fetch site settings');
        }
        
        const data = await response.json();
        console.log('[Site Settings] Data received:', data);
        return data;
      } catch (error) {
        console.error('[Site Settings] Error:', error);
        throw error;
      }
    },
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 300000, // Consider data valid for 5 minutes (300,000 ms)
    refetchInterval: 300000, // Only refetch every 5 minutes
  });

  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const getAppropriateLogoUrl = (isMobile = false) => {
    if (!settings) return null;

    if (isMobile) {
      return isDarkMode ? settings.logoMobileDark : settings.logoMobileLight;
    } else {
      return isDarkMode ? settings.logoDesktopDark : settings.logoDesktopLight;
    }
  };

  const getLogoHeight = (isMobile = false) => {
    if (!settings) return isMobile ? 40 : 60;

    let height;
    if (isMobile) {
      height = isDarkMode ? settings.logoHeightMobileDark : settings.logoHeightMobileLight;
    } else {
      height = isDarkMode ? settings.logoHeightDesktopDark : settings.logoHeightDesktopLight;
    }

    // Log the actual height for debugging
    const deviceType = isMobile ? 'mobile' : 'desktop';
    const themeType = isDarkMode ? 'dark' : 'light';
    console.log(`Getting logo height for ${deviceType}-${themeType}:`, height);

    return height;
  };

  return {
    settings,
    isLoading,
    getAppropriateLogoUrl,
    getLogoHeight,
    incentiveImageUrl: settings?.incentiveImageUrl || null,
    dailyOpenBeta: settings?.dailyOpenBeta ?? false,
    predictionsBetaOnly: settings?.predictionsBetaOnly ?? true,
    zapBetaOnly: settings?.zapBetaOnly ?? true,
    crateBetaOnly: settings?.crateBetaOnly ?? true,
    liveRoomsEnabled: settings?.liveRoomsEnabled ?? true,
  };
}
